import { WritingToolsJWT } from '@src/state/auth/types'
import siteConfig from '@src/state/site/siteConfig'
import { SiteState } from '@src/state/site/types'
import * as Cookie from 'js-cookie'
import { LOCAL_OR_PLAYGROUND } from './localOrPlayground'

export enum Cookies {
  // `V` is a cookie containing the unique id used to bucket users into optimizely experiments
  ExperimentIdentifier = 'V',
  // `userRole` is used to save the role of a user on the site
  UserRole = 'userRole',
  // `optUserProfile` is the user profile cookie for optimizely
  OptimizelyUserProfile = 'opt-user-profile',
  // `devoptUserProfile` is the user profile cookie for optimizely used in a dev environment
  DevOptimizelyUserProfile = 'devopt-user-profile',
  // `id_token` is the authentication id token of the user
  IdToken = 'id_token',
  // `exp` is a cookie used to track client experiments used when initializizing rio
  RioClientExperiments = `exp`,
  // `SU` is used to provide a session user id to the jericho service
  JerichoSession = `SU`,
  // `paper_uploaded` is used to track across applications whether a user has uploaded a paper through the wtai-upload-widget
  PaperUploaded = `paper_uploaded`,
  // `optimizely_test_cookie` is set to 'true' when a QA user adds a 'test-audience' query string key
  OptimizelyTestCookie = `optimizely_test_cookie`,
  // `ec_upload_pending` is set to indicate a paper has been submitted for Expert Check but may not yet be available to retrieval
  EcUploadPending = `ec_upload_pending`,
  // `opt-is-sem` is set on our landing pages via an optimizely web experiment (https://app.optimizely.com/v2/projects/17132650691/experiments/20849190098/shared_code) to indicate if the user landed on it via a url with the `c_id=sem` query param entry within the last 14 days
  FromSEM = 'opt-is-sem',
  // `opt-is-aff` is set on our landing pages via an optimizely web experiment (https://app.optimizely.com/v2/projects/17132650691/experiments/20849190098/shared_code) to indicate if the user landed on it via a url with the `utm_medium=Affiliate` query param entry within the last 14 days
  FromAffiliate = 'opt-is-aff',
  CwcRollout = 'cwc-rollout',
  EnableCLSandbox = 'enable-cl-sandbox',
  AccessToken = 'access_token',
  DFID = 'DFID',
  CSessionID = 'CSessionID',
  _sdsat_uvn = '_sdsat_uvn',
}

// optional configuration values for domain and expiration
interface CookieConfig {
  domain?: string
  expires?: number | Date
  secure?: true
}

// set a cookie using one of the Cookies enum values
export const setCookie = (cookie: Cookies, value: string, config?: CookieConfig) => {
  // this util was copied from https://gitlab.com/chegginc/learning-services/writing-tools/unified-citations-frontend/blob/main/core/utils/cookies.ts
  // todo: figure out what to do if we try to set the invalid cookie
  const cookieConfig = { ...config, secure: true }
  return Cookie.set(cookie, value, cookieConfig)
}

// get a cookie using one of the Cookies enum values
export const getCookie = (cookie: Cookies) => Cookie.get(cookie)

// remove a cookie using one of the Cookies enum values
export const removeCookie = (cookie: Cookies, site: SiteState) => {
  Cookie.remove(cookie, { domain: siteConfig(site, LOCAL_OR_PLAYGROUND).cookieDomain })
}

interface PaperUploadedStatus {
  sessionId: string
  cheggUserUuid?: string
}

export const userHasUploadedCookie = (jwt: WritingToolsJWT): boolean => {
  if (!jwt) {
    return false
  }

  try {
    const cookieUploadStatus = getCookie(Cookies.PaperUploaded) || '{}'
    const parsedUploadStatus: PaperUploadedStatus = JSON.parse(cookieUploadStatus)
    const {
      sessionId: cookieSessionId,
      cheggUserUuid: cookieCheggUserUuid,
    } = parsedUploadStatus

    const { sessionId: jwtSessionId, cheggUserUuid: jwtCheggUserUuid } = jwt

    return !!(
      jwtSessionId === cookieSessionId ||
      (jwtCheggUserUuid && jwtCheggUserUuid === cookieCheggUserUuid)
    )
  } catch {
    return false
  }
}

export const setPaperUploadedCookie = (site: SiteState, jwt: WritingToolsJWT): void => {
  try {
    const { sessionId, cheggUserUuid } = jwt
    const uploadStatus = JSON.stringify({
      sessionId,
      cheggUserUuid,
    })

    const cookieConfig = {
      domain: siteConfig(site, LOCAL_OR_PLAYGROUND).cookieDomain,
      expires: 7,
    }
    setCookie(Cookies.PaperUploaded, uploadStatus, cookieConfig)
  } catch {
    return
  }
}



// WEBPACK FOOTER //
// ./src/utils/cookies.ts